import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'react-bootstrap';
import TimeAgoWidget from "core/components/shared/TimeAgoWidget";
import UrlSourceWidget from './UrlSourceWidget';

interface UseCase {
  id: number;
  use_case_name: string;
  use_case_type: string;
  company: string;
  industry: string;
  tools: string;
  use_case_description: string;
  performance_impact: string;
  performance_improvement_category: string | null;
  geography: string | null;
  country: string | null;
  use_case_date: string;
  source: string;
  created_at: string;
  credibility_reasoning: string;
  credibility_score: number;
  is_credible: boolean;
  is_relevant: boolean;
  relevance_reasoning: string;
  relevance_score: number;
  url_validation_score: number;
}

interface UseCaseDetailsProps {
  useCase: any;
  show: boolean;
  onHide: () => void;
}

const UseCaseDetails: React.FC<UseCaseDetailsProps> = ({ useCase, show, onHide }) => {
  if (!useCase) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const renderScore = (score: number | null) => {
    if (score === null) return 'N/A';
    return `${score}/10`;
  };

  const renderBoolean = (value: boolean | null) => {
    if (value === null) return 'N/A';
    return value ? 'Yes' : 'No';
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <ModalHeader closeButton>
        <p className="modal-title line-height-2">{useCase.use_case_name}</p>
      </ModalHeader>
      <ModalBody>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Basic Information</h6>
                <dl className="row mb-0">
                  <dt className="col-sm-4">ID</dt>
                  <dd className="col-sm-8">{useCase.id}</dd>
                  
                  <dt className="col-sm-4">Type</dt>
                  <dd className="col-sm-8">{useCase.use_case_type}</dd>
                  
                  <dt className="col-sm-4">Company</dt>
                  <dd className="col-sm-8">{useCase.company}</dd>
                  
                  <dt className="col-sm-4">Industry</dt>
                  <dd className="col-sm-8">{useCase.industry || 'N/A'}</dd>
                  
                  <dt className="col-sm-4">Date</dt>
                  <dd className="col-sm-8">{formatDate(useCase.use_case_date)}</dd>
                  
                  <dt className="col-sm-4">Created</dt>
                  <dd className="col-sm-8"><TimeAgoWidget date={useCase.created_at} /></dd>
                </dl>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Credibility & Relevance</h6>
                <dl className="row mb-0">
                  <dt className="col-sm-4">Credibility</dt>
                  <dd className="col-sm-8">{renderBoolean(useCase.is_credible)}</dd>
                  
                  <dt className="col-sm-4">Credibility Score</dt>
                  <dd className="col-sm-8">{renderScore(useCase.credibility_score)}</dd>
                  
                  <dt className="col-sm-4">Relevance</dt>
                  <dd className="col-sm-8">{renderBoolean(useCase.is_relevant)}</dd>
                  
                  <dt className="col-sm-4">Relevance Score</dt>
                  <dd className="col-sm-8">{renderScore(useCase.relevance_score)}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Tools</h6>
                <div className="mb-3">
                  {useCase.tools?.split(',').map((tool: string, index: number) => (
                    <span key={index} className="badge bg-primary me-1 mb-1" style={{ maxWidth: '150px', fontWeight: 'normal', fontSize: '12px', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                      {tool.trim()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Description</h6>
                <p className="card-text">{useCase.use_case_description}</p>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Performance Impact</h6>
                <dl className="row mb-0">
                  <dt className="col-sm-3">Impact</dt>
                  <dd className="col-sm-9">{useCase.performance_impact}</dd>
                  
                  <dt className="col-sm-3">Improvement Category</dt>
                  <dd className="col-sm-9">{useCase.performance_improvement_category || 'N/A'}</dd>
                  
                  <dt className="col-sm-3">Geography</dt>
                  <dd className="col-sm-9">{useCase.geography || 'N/A'}</dd>
                  
                  <dt className="col-sm-3">Country</dt>
                  <dd className="col-sm-9">{useCase.country || 'N/A'}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Credibility Reasoning</h6>
                <p className="card-text">{useCase.credibility_reasoning || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Relevance Reasoning</h6>
                <p className="card-text">{useCase.relevance_reasoning || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">Source</h6>
                {useCase.source ? (
                  <UrlSourceWidget source={useCase.source} urlValidationScore={useCase.url_validation_score} />
                ) : (
                  'N/A'
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UseCaseDetails; 